<i18n>
{
  "fr": {
    "user-dashboard-title": "Tableau de bord",
    "user-dashboard-project-compliance": "Conformité par projet",
    "user-dashboard-project-compliance-text": "Suivez la progression des projets pour identifier les priorités et assurer une gestion optimale des tâches en cours.",
    "user-dashboard-add-projet": "Ajouter un projet",
    "user-dashboard-all-projets": "Voir tous les projets"
  },
  "en": {
    "user-dashboard-title": "Dashboard",
    "user-dashboard-project-compliance": "Project compliance",
    "user-dashboard-project-compliance-text": "Suivez la progression des projets pour identifier les priorités et assurer une gestion optimale des tâches en cours.",
    "user-dashboard-add-projet": "Add a project",
    "user-dashboard-all-projets": "See all projects"
  }
}
</i18n>
<template>
  <div>
    <ui-title>{{ $t('user-dashboard-title') }}</ui-title>

    <prismic-content doctype="user-dashboard" v-slot="{ content }">
      <!-- Info box -->
      <b-message
        v-if="$prismicHasText(content.alert_message)"
        type="is-info"
        icon-size="is-medium"
        has-icon
        class="has-background-white"
      >
        <prismic-rich-text :field="content.alert_message" class="has-max-width-768 has-text-grey-dark" />
      </b-message>

      <s-loader :active="!dashboardData && $apollo.queries.infos.loading" />
      <template v-if="dashboardData">
        <!-- First visit -->
        <first-visit-box v-if="!hasStartedUsingPlatform" :prismic-content="content" />

        <!-- Dashboard -->
        <template v-else>
          <div class="columns">
            <!-- General compliance stats -->
            <div v-if="hasAccessToGeneralCompliance" class="column">
              <general-compliance-chart :data="dashboardData.generalComplianceStats" />
            </div>
            <!-- Global projects compliance stats -->
            <div v-if="hasAccessToProjectsCompliance" class="column">
              <project-compliance-chart :data="dashboardData.projectsComplianceStats" />
            </div>
          </div>

          <!-- Individual projects compliance stats -->
          <template v-if="hasAnyProject">
            <div class="card mt-24px">
              <ui-box>
                <h2 class="title is-4 has-text-weight-bold mb-0">{{ $t('user-dashboard-project-compliance') }}</h2>
                <p class="mt-12px mb-32px">
                  {{ $t('user-dashboard-project-compliance-text') }}
                </p>

                <!-- Projects slider -->
                <b-carousel
                  :indicator="false"
                  :arrow="$media.mobile && projectsCarousel.length > 1"
                  :arrow-hover="false"
                  :autoplay="false"
                  ref="projectsCarousel"
                >
                  <b-carousel-item v-for="(slide, i) in projectsCarousel" :key="i">
                    <div class="columns">
                      <div class="column is-flex" v-for="project in slide" :key="project.id">
                        <project-snippet :data="project" :clickable="hasAccessToProjectsCompliance" />
                      </div>
                    </div>
                  </b-carousel-item>
                </b-carousel>

                <div class="level mt-24px">
                  <div class="level-left">
                    <b-button
                      v-if="hasAccessToProjectsCompliance"
                      tag="router-link"
                      :to="{ name: $consts.urls.URL_PROJECT_ADD }"
                      type="is-link"
                      outlined
                      icon-left="plus-circle"
                      >{{ $t('user-dashboard-add-projet') }}</b-button
                    >
                  </div>
                  <div class="level-right">
                    <router-link v-if="hasAccessToProjectsCompliance" :to="{ name: $consts.urls.URL_PROJECT_LIST }">
                      {{ $t('user-dashboard-all-projets') }}
                    </router-link>
                    <div v-if="projectsCarousel.length > 1" class="is-inline-block is-hidden-mobile ml-16px">
                      <b-button outlined icon-right="chevron-left" @click="projectsCarouselPrev" />
                      <b-button outlined icon-right="chevron-right" class="ml-8px" @click="projectsCarouselNext" />
                    </div>
                  </div>
                </div>
              </ui-box>
            </div>
          </template>
        </template>
      </template>

      <!-- Resources -->
      <div class="card mt-24px">
        <resources :prismic-content="content" />
      </div>
    </prismic-content>
  </div>
</template>

<script>
import BusinessService from '@/services/business'
import i18n from '@/helpers/i18n'

import Breadcrumbs from '@/mixins/breadcrumbs'

import PrismicContent from '@/components/prismic/prismic-content'
import FirstVisitBox from '@/components/dashboards/first-visit-box'
import GeneralComplianceChart from '@/components/dashboards/general-compliance-chart'
import ProjectComplianceChart from '@/components/dashboards/project-compliance-chart'
import ProjectSnippet from '@/components/dashboards/project-snippet'
import Resources from '@/components/dashboards/resources'
import UiBox from '@/components/ui/box'
import UiTitle from '@/components/ui/title'

import {
  BUSINESS_USER_TYPE_GLOBAL_EDITOR,
  BUSINESS_USER_TYPE_BUSINESS_EDITOR,
  BUSINESS_USER_TYPE_BUSINESS_ADMIN,
  BUSINESS_USER_TYPE_PROJECT_EDITOR
} from '@/consts/enums'

export default {
  mixins: [Breadcrumbs],
  components: {
    PrismicContent,
    FirstVisitBox,
    GeneralComplianceChart,
    ProjectComplianceChart,
    ProjectSnippet,
    Resources,
    UiBox,
    UiTitle
  },
  props: {
    userType: {
      type: String
    }
  },
  data() {
    return {
      businessId: BusinessService.getCurrentBusiness().id,
      dashboardData: null,
      currentUserType: ''
    }
  },
  computed: {
    breadcrumbs() {
      return [{ label: this.$t('user-dashboard-title') }]
    },
    hasStartedUsingPlatform() {
      return this.dashboardData.business.compliance.hasActivity || this.dashboardData.latestProjects.items.length > 0
    },
    hasAnyProject() {
      return this.dashboardData.latestProjects.items.length > 0 || this.dashboardData.latestProjectTemplates.length > 0
    },
    projectsCarousel() {
      const itemsPerSlide = this.$media.fullhd ? 4 : this.$media.tablet ? 3 : 1
      const slides = []
      let slideIndex = 0
      let itemCounter = 0

      const projects = [...this.dashboardData.latestProjectTemplates, ...this.dashboardData.latestProjects.items]

      projects.forEach((item) => {
        // Create slide
        if (!slides[slideIndex]) slides[slideIndex] = []
        // Append project to slide
        slides[slideIndex].push(item)
        // Slide full, next slide
        if (++itemCounter == itemsPerSlide) {
          itemCounter = 0
          slideIndex++
        }
      })

      return slides
    },
    hasAccessToGeneralCompliance() {
      return [BUSINESS_USER_TYPE_GLOBAL_EDITOR, BUSINESS_USER_TYPE_BUSINESS_EDITOR, BUSINESS_USER_TYPE_BUSINESS_ADMIN].includes(
        this.currentUserType
      )
    },
    hasAccessToProjectsCompliance() {
      return [BUSINESS_USER_TYPE_GLOBAL_EDITOR, BUSINESS_USER_TYPE_PROJECT_EDITOR, BUSINESS_USER_TYPE_BUSINESS_ADMIN].includes(
        this.currentUserType
      )
    }
  },
  methods: {
    projectsCarouselNext() {
      this.$refs.projectsCarousel.next()
    },
    projectsCarouselPrev() {
      this.$refs.projectsCarousel.prev()
    }
  },
  apollo: {
    infos: {
      query() {
        return this.$withFragments(
          this.$options.query.UserDashboard_Infos,
          GeneralComplianceChart.fragments.generalComplianceChart,
          ProjectComplianceChart.fragments.projectComplianceChart,
          ProjectSnippet.fragments.projectSnippet
        )
      },
      variables() {
        return {
          businessId: this.businessId,
          language: i18n.contentLanguage()
        }
      },
      skip() {
        return !this.businessId
      },
      update(data) {
        this.dashboardData = data
      }
    },
    userType: {
      query() {
        return this.$options.query.UserDashboard_UserType
      },
      variables() {
        return {
          businessId: BusinessService.getCurrentBusiness().id
        }
      },
      update(data) {
        if (data && data.me && data.me.userBusiness && data.me.userBusiness.length > 0) {
          this.currentUserType = data.me.userBusiness[0].userType
        }
      },
      fetchPolicy: 'cache-first'
    }
  }
}
</script>

<graphql>
query UserDashboard_UserType($businessId: ID!) {
  me {
    id
    userBusiness(businessId: { value: $businessId }) {
      id
      userType
    }
  }
}

query UserDashboard_Infos($businessId: ID!, $language: ContentLanguage!) {
  generalComplianceStats: searchIndicatorsStats(
    language: $language
    businessId: $businessId
    filter: { type: GENERAL, projectStatus: { notIn: [INACTIVE, FINISH] } }
  ) {
    ...generalComplianceChart
  }

  projectsComplianceStats: searchIndicatorsStats(
    language: $language
    businessId: $businessId
    filter: { type: PROJECT, projectStatus: { notIn: [INACTIVE, FINISH] } }
  ) {
    ...projectComplianceChart
  }

  business(id: $businessId) {
    id
    compliance(language: DEFAULT) {
      hasActivity
    }
  }

  latestProjects: searchProjects(businessId: $businessId, sort: CREATED_AT, order: DESC, filter: { status: { eq: ACTIVE } }) {
    items {
      id
      ...projectSnippet
    }
  }

  latestProjectTemplates: projectTemplates(businessId: $businessId) {
    id
    ...projectSnippet
  }
}
</graphql>
